import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  FolderOpen,
  Search,
  Loader2,
  Plus,
  Trash2,
  ChevronRight,
  Calendar,
  Scale,
  AlertCircle,
} from 'lucide-react';
import { apiClient, Case } from '../api/client';

export default function MyCases() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ caseNumber: string; title: string; court: string; status: string }[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  // Fetch user's registered cases
  const { data: myCases = [], isLoading } = useQuery<Case[]>({
    queryKey: ['user-cases'],
    queryFn: () => apiClient.getUserCases(),
  });

  // Register to a case
  const registerMutation = useMutation({
    mutationFn: (caseNumber: string) => apiClient.registerToCase(caseNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-cases'] });
      setSearchResults([]);
      setSearchQuery('');
      setSearched(false);
    },
  });

  // Unregister from a case
  const unregisterMutation = useMutation({
    mutationFn: (caseNumber: string) => apiClient.unregisterFromCase(caseNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-cases'] });
    },
  });

  const handleSearch = async () => {
    if (searchQuery.trim().length < 2) return;
    setSearching(true);
    setSearched(false);
    try {
      const results = await apiClient.searchPublicCases(searchQuery.trim());
      // Filter out cases already registered
      const registeredNumbers = new Set(myCases.map(c => c.caseNumber));
      setSearchResults(results.filter(r => !registeredNumbers.has(r.caseNumber)));
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
      setSearched(true);
    }
  };

  const statusBadge = (status: string) => {
    const base = 'px-2 py-0.5 rounded text-xs font-medium';
    if (status === 'Active') return <span className={`${base} bg-green-100 text-green-700`}>Active</span>;
    if (status === 'Pending Review') return <span className={`${base} bg-orange-100 text-orange-700`}>Pending Review</span>;
    return <span className={`${base} bg-gray-100 text-gray-600`}>{status}</span>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Cases</h1>
        <p className="mt-1 text-gray-500 text-sm">
          Cases you're registered to. Click a case to view details and submit evidence.
        </p>
      </div>

      {/* My Registered Cases */}
      <section>
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
          Registered Cases ({myCases.length})
        </h2>
        
        {myCases.length === 0 ? (
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 text-center">
            <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No cases yet</h3>
            <p className="text-sm text-gray-500">
              Search for a case below to register and start submitting evidence.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {myCases.map(c => (
              <div
                key={c.caseNumber}
                className="bg-white rounded-xl border border-gray-200 hover:border-primary-300 transition-colors group"
              >
                <div className="flex items-center p-4">
                  <Link
                    to={`/cases/${c.caseNumber}`}
                    className="flex-1 flex items-center gap-4 min-w-0"
                  >
                    <div className="p-2 bg-primary-50 rounded-lg flex-shrink-0 group-hover:bg-primary-100 transition-colors">
                      <FolderOpen className="h-5 w-5 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{c.caseNumber}</span>
                        {statusBadge(c.status)}
                      </div>
                      <p className="text-sm text-gray-600 truncate">{c.title}</p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                        {c.court && (
                          <span className="flex items-center gap-1">
                            <Scale className="h-3 w-3" />
                            {c.court}
                          </span>
                        )}
                        {c.filingDate && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Filed {new Date(c.filingDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-600 flex-shrink-0" />
                  </Link>
                  <button
                    onClick={() => unregisterMutation.mutate(c.caseNumber)}
                    disabled={unregisterMutation.isPending}
                    className="ml-2 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                    title="Remove from My Cases"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Search and Register */}
      <section className="pt-4 border-t border-gray-200">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
          Find a Case to Register
        </h2>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Case number or name (e.g. 2026-CV-0156)"
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={searching || searchQuery.trim().length < 2}
            className="px-4 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 flex-shrink-0"
          >
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Search
          </button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="mt-3 space-y-2">
            {searchResults.map(c => (
              <div
                key={c.caseNumber}
                className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-4"
              >
                <div className="p-2 bg-gray-100 rounded-lg flex-shrink-0">
                  <FolderOpen className="h-5 w-5 text-gray-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm">{c.caseNumber}</span>
                    {statusBadge(c.status)}
                  </div>
                  <p className="text-sm text-gray-600 truncate">{c.title}</p>
                  {c.court && <p className="text-xs text-gray-500">{c.court}</p>}
                </div>
                <button
                  onClick={() => registerMutation.mutate(c.caseNumber)}
                  disabled={registerMutation.isPending}
                  className="px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1.5 flex-shrink-0"
                >
                  {registerMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Register
                </button>
              </div>
            ))}
          </div>
        )}

        {searched && !searching && searchResults.length === 0 && (
          <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-500 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>
              No cases found for "<strong>{searchQuery}</strong>". Contact court administration if you believe your case should appear here.
            </span>
          </div>
        )}
      </section>
    </div>
  );
}
